import { useState, useEffect } from "react";
import apiClient from "../config/axiosConfig";
import {
  isAuthenticated,
  getUserId,
  getUserName,
  getUserRole,
} from "../utils/authStorage";

const useCumpleanosNotificacion = () => {
  const [cumpleaneros, setCumpleaneros] = useState([]);
  const [esCumpleanos, setEsCumpleanos] = useState(false);
  const [mostrarNotificacion, setMostrarNotificacion] = useState(false);
  const [cargando, setCargando] = useState(false);
  const [error, setError] = useState(null);

  const getUserIdFromToken = () => getUserId();

  const getUserNameFromToken = () => getUserName();

  const getRoleFromToken = () => {
    const role = getUserRole();
    if (role === null) return null;

    // Comprobar si el usuario es desarrollador (rol 5)
    if (role === 5) {
      const developerRole = sessionStorage.getItem("RolDevelop");
      if (developerRole) {
        return Number(developerRole);
      }
    }

    return role;
  };

  // Verificar si hay cumpleañeros hoy
  const verificarCumpleanos = async () => {
    const userId = getUserIdFromToken();
    if (!userId) return;

    // No mostrar a supervisores (rol 3)
    const rolActual = getRoleFromToken();
    if (rolActual === 3) return;

    // Comprobar si ya se ha mostrado la notificación de cumpleaños hoy
    const fechaHoy = new Date().toLocaleDateString();
    const cumpleanosRevisados = localStorage.getItem("cumpleanosRevisados");

    if (cumpleanosRevisados === fechaHoy) {
      return;
    }

    setCargando(true);

    try {
      // 1. Verificar propio cumpleaños
      const esMiCumple = await verificarCumpleanosPropioYRetornar();

      // 2. Verificar otros cumpleañeros
      const response = await apiClient.get("/users/cumpleanos/hoy");

      let otrosCumpleaneros = [];

      if (response.data) {
        // Filtrar al usuario actual si existe
        otrosCumpleaneros = response.data.filter(
          (user) => user.idusuario !== userId,
        );

        setCumpleaneros(otrosCumpleaneros);
      } else {
        console.warn(`API de cumpleaños respondió sin datos`);
      }

      // 3. Tomar decisión de mostrar notificación
      const deberiasMostrar = esMiCumple || otrosCumpleaneros.length > 0;

      if (deberiasMostrar) {
        setMostrarNotificacion(true);
      }

      // Marcar como revisado para hoy
      localStorage.setItem("cumpleanosRevisados", fechaHoy);
    } catch (error) {
      // 401 = sesión expirada, no es un error real; no marcar como revisado para reintentar tras re-login
      if (error.response?.status === 401) {
        console.warn("Sesión expirada, se omite verificación de cumpleaños");
      } else {
        console.error("Error verificando cumpleaños:", error);
        setError(error.message);
      }
    } finally {
      setCargando(false);
    }
  };

  // Verificar el propio cumpleaños
  const verificarCumpleanosPropioYRetornar = async () => {
    const userId = getUserIdFromToken();
    if (!userId) return false;

    try {
      const response = await apiClient.get(`/users/${userId}`);

      const userData = response.data;

      // Añadir hora para evitar problemas con UTC
      const fechaSinHora = userData.fecha_cumpleaños;
      const fechaCumpleanos = new Date(`${fechaSinHora}T12:00:00Z`); // Usar mediodía UTC
      const hoy = new Date();

      // Obtener componentes de fecha usando UTC para ambas fechas
      const diaCumple = fechaCumpleanos.getUTCDate();
      const mesCumple = fechaCumpleanos.getUTCMonth() + 1; // +1 porque getUTCMonth devuelve 0-11

      const diaHoy = hoy.getDate();
      const mesHoy = hoy.getMonth() + 1; // +1 porque getMonth devuelve 0-11

      // Crear strings en formato MM-DD pero usando los métodos UTC para la fecha de cumpleaños
      const fechaCumpleMesDia = `${mesCumple}-${diaCumple}`;
      const hoyMesDia = `${mesHoy}-${diaHoy}`;

      // Comparar los strings de fecha
      const esCumple = fechaCumpleMesDia === hoyMesDia;

      // Establecer estado
      setEsCumpleanos(esCumple);

      return esCumple;
    } catch (error) {
      if (error.response?.status === 401) {
        console.warn("Sesión expirada, se omite verificación de cumpleaños propio");
      } else {
        console.error("Error verificando cumpleaños propio:", error);
      }
      return false;
    }
  };

  const cerrarNotificacion = () => {
    setMostrarNotificacion(false);
  };

  return {
    cumpleaneros,
    esCumpleanos,
    mostrarNotificacion,
    cargando,
    error,
    nombreUsuario: getUserNameFromToken(),
    verificarCumpleanos,
    cerrarNotificacion,
  };
};

export default useCumpleanosNotificacion;
